class KvCustomBlog extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[ref="grid"]') || this.querySelector('.kv-custom-blog__grid');
    this.featured = this.querySelector('[ref="featured"]') || this.querySelector('.kv-custom-blog__featured');
    this.loadMoreBtn =
      this.querySelector('[ref="loadMore"]') || this.querySelector('.kv-custom-blog__load-more');
    this.loadMoreWrap =
      this.querySelector('[ref="loadMoreWrap"]') || this.querySelector('.kv-custom-blog__load-more-wrap');
    this.searchInput =
      this.querySelector('[ref="search"]') ||
      this.querySelector('[data-kv-search]') ||
      this.querySelector('.kv-custom-blog__search-input');
    this.tagButtons = this.querySelectorAll('[data-kv-tag]');
    this.emptyState = this.querySelector('[ref="empty"]') || this.querySelector('.kv-custom-blog__empty');
    this.activeTag = 'all';
    this.activeTagHandle = '';
    this.searchQuery = '';
    this.isLoadingAll = false;
    this.allViewSnapshot = null;
    this.tagLoadToken = 0;

    this.tagButtons.forEach((button) => {
      button.addEventListener('click', () => this.onTagClick(button));
    });

    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => this.onSearchInput());
      this.searchInput.addEventListener('search', () => this.onSearchInput());
    }

    if (this.loadMoreBtn) {
      this.loadMoreBtn.addEventListener('click', (event) => this.onLoadMore(event));
    }

    this.applyFilters();
    this.captureInitialState();
    this.saveAllViewSnapshot();
    this.syncLoadMoreState();
  }

  captureInitialState() {
    this.initialItemUrls = new Set();

    this.getItems().forEach((item) => {
      if (item.getAttribute('data-initial') !== 'true') return;
      const url = this.normalizeArticleUrl(item.getAttribute('data-url'));
      if (url) this.initialItemUrls.add(url);
    });

    if (this.loadMoreBtn) {
      this.initialLoadMoreState = {
        hasMore: this.loadMoreBtn.getAttribute('data-has-more') === 'true',
        nextUrl: this.loadMoreBtn.getAttribute('data-next-url') || '',
        nextPage: this.loadMoreBtn.getAttribute('data-next-page') || '',
      };
    }
  }

  getLoadMoreSnapshot() {
    if (!this.loadMoreBtn) return null;

    return {
      hasMore: this.loadMoreBtn.getAttribute('data-has-more') === 'true',
      nextUrl: this.loadMoreBtn.getAttribute('data-next-url') || '',
      nextPage: this.loadMoreBtn.getAttribute('data-next-page') || '',
    };
  }

  applyLoadMoreSnapshot(snapshot) {
    if (!this.loadMoreBtn || !snapshot) return;

    if (snapshot.hasMore && snapshot.nextUrl) {
      this.setLoadMoreState({
        hasMore: true,
        nextUrl: snapshot.nextUrl,
        nextPage: Number.parseInt(snapshot.nextPage || '0', 10) || undefined,
      });
      return;
    }

    this.setLoadMoreState({ hasMore: false });
  }

  saveAllViewSnapshot(force = false) {
    if (!this.grid) return;
    if (!force && this.activeTag !== 'all') return;

    this.allViewSnapshot = {
      html: this.grid.innerHTML,
      loadMore: this.getLoadMoreSnapshot(),
    };
  }

  restoreAllViewSnapshot() {
    if (this.featured) {
      this.featured.hidden = false;
    }

    if (this.allViewSnapshot?.html != null && this.grid) {
      this.grid.innerHTML = this.allViewSnapshot.html;
      this.applyLoadMoreSnapshot(this.allViewSnapshot.loadMore || this.initialLoadMoreState);
      return;
    }

    this.resetAllViewToInitial();
  }

  resetAllViewToInitial() {
    this.getItems().forEach((item) => {
      const url = this.normalizeArticleUrl(item.getAttribute('data-url'));
      const isInitial = item.getAttribute('data-initial') === 'true' || this.initialItemUrls?.has(url);

      if (isInitial) {
        item.hidden = false;
        item.classList.remove('kv-custom-blog__item--hidden');
        item.removeAttribute('data-kv-source');
        return;
      }

      item.remove();
    });

    if (this.featured) {
      this.featured.hidden = false;
    }

    this.applyLoadMoreSnapshot(this.initialLoadMoreState);
  }

  isLoadMoreAllowed() {
    return this.activeTag === 'all' && !this.searchQuery;
  }

  normalizeArticleUrl(url) {
    if (!url) return '';

    try {
      return new URL(url, window.location.origin).pathname;
    } catch {
      return url.split('?')[0];
    }
  }

  getLoadMoreCount() {
    return Number.parseInt(this.grid?.getAttribute('data-load-more-count') || '3', 10);
  }

  getInitialCount() {
    return Number.parseInt(this.grid?.getAttribute('data-initial-count') || '3', 10);
  }

  getInitialGridCount() {
    return Number.parseInt(this.grid?.getAttribute('data-initial-grid-count') || '3', 10);
  }

  getTotalArticles() {
    return Number.parseInt(this.grid?.getAttribute('data-total-articles') || '0', 10);
  }

  getGridTotal() {
    const gridTotal = Number.parseInt(this.grid?.getAttribute('data-grid-total') || '0', 10);
    if (gridTotal) return gridTotal;

    const totalArticles = this.getTotalArticles();
    return totalArticles > 0 ? Math.max(0, totalArticles - 1) : 0;
  }

  getFeaturedUrl() {
    const featuredFromGrid = this.grid?.getAttribute('data-featured-url');
    if (featuredFromGrid) return this.normalizeArticleUrl(featuredFromGrid);

    const featuredLink = this.featured?.querySelector('[data-kv-featured-title]')?.getAttribute('href');
    return this.normalizeArticleUrl(featuredLink || '');
  }

  isFeaturedArticleUrl(url) {
    const featuredUrl = this.getFeaturedUrl();
    return Boolean(featuredUrl && url === featuredUrl);
  }

  getBlogPageUrl(pageNumber) {
    const blogUrl = this.getAttribute('data-blog-url');
    if (!blogUrl || !pageNumber) return '';

    const separator = blogUrl.includes('?') ? '&' : '?';
    return `${blogUrl}${separator}page=${pageNumber}`;
  }

  async onTagClick(button) {
    const previousTag = this.activeTag;
    this.activeTag = button.getAttribute('data-kv-tag') || 'all';
    this.activeTagHandle = button.getAttribute('data-kv-tag-handle') || '';

    this.tagButtons.forEach((btn) => {
      const isActive = btn === button;
      btn.classList.toggle('kv-custom-blog__tag--active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    if (this.activeTag === 'all') {
      this.tagLoadToken += 1;

      if (this.searchInput) {
        this.searchInput.value = '';
      }
      this.searchQuery = '';

      this.restoreAllViewSnapshot();
      this.applyFilters();
      return;
    }

    if (previousTag === 'all') {
      this.saveAllViewSnapshot(true);
    }

    this.applyFilters();

    const loadToken = ++this.tagLoadToken;
    await this.loadPostsForActiveTag(loadToken);

    if (loadToken !== this.tagLoadToken || this.activeTag === 'all') return;

    this.applyFilters();
  }

  async onSearchInput() {
    this.searchQuery = this.searchInput.value.trim().toLowerCase();
    this.applyFilters();

    if (this.searchQuery) {
      await this.ensureAllPostsLoaded('search');
      this.applyFilters();
    }
  }

  getItems() {
    return Array.from(this.querySelectorAll('.kv-custom-blog__item'));
  }

  getLoadedUrls() {
    return new Set(
      this.getItems()
        .map((item) => this.normalizeArticleUrl(item.getAttribute('data-url')))
        .filter(Boolean)
    );
  }

  getLoadedGridUrls() {
    return new Set(
      this.getItems()
        .map((item) => this.normalizeArticleUrl(item.getAttribute('data-url')))
        .filter((url) => url && !this.isFeaturedArticleUrl(url))
    );
  }

  articleHasActiveTag(tags) {
    if (this.activeTag === 'all') return true;

    const activeLower = this.activeTag.toLowerCase();
    const activeHandle = (this.activeTagHandle || '').toLowerCase();

    return tags.some((tag) => {
      const tagLower = tag.toLowerCase();
      const tagHandle = tagLower.replace(/\s+/g, '-');
      return tagLower === activeLower || tagHandle === activeHandle;
    });
  }

  itemMatches(item) {
    const tags = (item.getAttribute('data-tags') || '')
      .toLowerCase()
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);

    const title = (item.getAttribute('data-title') || '').toLowerCase();
    const excerpt = (item.getAttribute('data-excerpt') || '').toLowerCase();
    const tagMatch = this.articleHasActiveTag(tags);
    const searchMatch =
      !this.searchQuery ||
      title.includes(this.searchQuery) ||
      excerpt.includes(this.searchQuery) ||
      tags.some((tag) => tag.includes(this.searchQuery));

    return tagMatch && searchMatch;
  }

  applyFilters() {
    const items = this.getItems();
    const isFiltered = this.activeTag !== 'all' || Boolean(this.searchQuery);
    let visibleCount = 0;

    items.forEach((item) => {
      const match = this.itemMatches(item);
      item.hidden = !match;
      item.classList.toggle('kv-custom-blog__item--hidden', !match);
      if (match) visibleCount += 1;
    });

    this.classList.toggle('kv-custom-blog--filtered', isFiltered);

    if (this.featured) {
      this.featured.hidden = isFiltered;
    }

    if (this.emptyState) {
      this.emptyState.hidden = visibleCount > 0;
    }

    this.updateLoadMoreVisibility();
  }

  updateLoadMoreVisibility() {
    if (!this.loadMoreBtn) return;

    const onAllTab = this.isLoadMoreAllowed();
    const hasMore = this.hasMoreArticles();
    const isExhausted = onAllTab && !hasMore;

    this.classList.toggle('kv-custom-blog--no-more', isExhausted);

    if (onAllTab) {
      this.loadMoreBtn.hidden = false;
      this.loadMoreBtn.classList.remove('kv-custom-blog__load-more--hidden');
      this.loadMoreBtn.classList.toggle('kv-custom-blog__load-more--exhausted', isExhausted);
      this.loadMoreBtn.disabled = isExhausted;
      this.loadMoreBtn.setAttribute('aria-disabled', isExhausted ? 'true' : 'false');
      this.loadMoreBtn.setAttribute('aria-hidden', 'false');

      if (this.loadMoreWrap) {
        this.loadMoreWrap.hidden = false;
        this.loadMoreWrap.classList.remove('kv-custom-blog__load-more-wrap--hidden');
        this.loadMoreWrap.setAttribute('aria-hidden', 'false');
      }

      return;
    }

    if (this.loadMoreBtn) {
      this.loadMoreBtn.hidden = true;
      this.loadMoreBtn.classList.add('kv-custom-blog__load-more--hidden');
      this.loadMoreBtn.classList.remove('kv-custom-blog__load-more--exhausted');
      this.loadMoreBtn.disabled = false;
      this.loadMoreBtn.setAttribute('aria-hidden', 'true');
    }

    if (this.loadMoreWrap) {
      this.loadMoreWrap.hidden = true;
      this.loadMoreWrap.classList.add('kv-custom-blog__load-more-wrap--hidden');
      this.loadMoreWrap.setAttribute('aria-hidden', 'true');
    }
  }

  setLoadMoreState({ hasMore, nextUrl, nextPage }) {
    if (!this.loadMoreBtn) return;

    if (hasMore && nextUrl) {
      this.loadMoreBtn.setAttribute('data-has-more', 'true');
      this.loadMoreBtn.setAttribute('data-next-url', nextUrl);
      if (nextPage) {
        this.loadMoreBtn.setAttribute('data-next-page', String(nextPage));
      }
    } else {
      this.loadMoreBtn.setAttribute('data-has-more', 'false');
      this.loadMoreBtn.removeAttribute('data-next-url');
      this.loadMoreBtn.removeAttribute('data-next-page');
    }

    this.updateLoadMoreVisibility();
  }

  allArticlesLoaded() {
    if (!this.grid) return true;

    const gridTotal = this.getGridTotal();
    if (!gridTotal) return false;

    return this.getLoadedGridUrls().size >= gridTotal;
  }

  getRemainingCount() {
    return Math.max(0, this.getGridTotal() - this.getLoadedGridUrls().size);
  }

  hasMoreArticles() {
    return this.getRemainingCount() > 0;
  }

  getNextPageFromLoadedCount() {
    if (this.allArticlesLoaded()) return null;

    const loadedGridCount = this.getLoadedGridUrls().size;
    const loadMoreCount = this.getLoadMoreCount();
    const nextBlogPosition = loadedGridCount + 2;

    return Math.ceil(nextBlogPosition / loadMoreCount);
  }

  syncLoadMoreState() {
    if (!this.loadMoreBtn) return;

    if (!this.isLoadMoreAllowed()) {
      this.updateLoadMoreVisibility();
      return;
    }

    if (!this.hasMoreArticles()) {
      this.setLoadMoreState({ hasMore: false });
      return;
    }

    const nextPage = this.getNextPageFromLoadedCount();
    if (!nextPage) {
      this.setLoadMoreState({ hasMore: false });
      return;
    }

    this.setLoadMoreState({
      hasMore: true,
      nextUrl: this.getBlogPageUrl(nextPage),
      nextPage,
    });
  }

  async loadPostsForActiveTag(loadToken) {
    if (this.activeTag === 'all') return;

    const blogUrl = this.getAttribute('data-blog-url');
    if (blogUrl && this.activeTagHandle) {
      await this.fetchAllTaggedPosts(`${blogUrl}/tagged/${this.activeTagHandle}`, loadToken);
      return;
    }

    await this.ensureAllPostsLoaded('tag', loadToken);
  }

  async fetchAllTaggedPosts(taggedUrl, loadToken) {
    if (this.isLoadingAll) return;

    this.isLoadingAll = true;
    this.classList.add('kv-custom-blog--loading-tag');

    try {
      const taggedBase = taggedUrl.split('?')[0];
      let lastPage = 1;

      for (let page = 1; page <= lastPage; page += 1) {
        if (loadToken != null && loadToken !== this.tagLoadToken) break;

        const pageUrl = page === 1 ? taggedBase : `${taggedBase}?page=${page}`;
        const html = await this.fetchSectionHtml(pageUrl);

        if (loadToken != null && loadToken !== this.tagLoadToken) break;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const section = this.findBlogSection(doc);
        if (!section) break;

        if (page === 1) {
          const fetchedGrid =
            section.querySelector('[ref="grid"]') || section.querySelector('.kv-custom-blog__grid');
          lastPage = Number.parseInt(fetchedGrid?.getAttribute('data-last-page') || '1', 10);
        }

        if (loadToken != null && loadToken !== this.tagLoadToken) break;

        this.appendItemsFromSection(section, Infinity, 'tag');
      }
    } catch (error) {
      console.error(error);
      if (loadToken == null || loadToken === this.tagLoadToken) {
        await this.ensureAllPostsLoaded('tag', loadToken);
      }
    } finally {
      this.isLoadingAll = false;
      this.classList.remove('kv-custom-blog--loading-tag');
    }
  }

  async ensureAllPostsLoaded(source = 'all', loadToken = null) {
    if (this.allArticlesLoaded() || this.isLoadingAll || !this.loadMoreBtn) return;

    this.isLoadingAll = true;

    try {
      let safety = 0;
      while (this.hasMoreArticles() && safety < 50) {
        if (loadToken != null && loadToken !== this.tagLoadToken) break;

        const addedCount = await this.fetchNextPage(source);
        safety += 1;
        if (this.allArticlesLoaded()) break;
        if (addedCount === 0 && !this.hasMoreArticles()) break;
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.isLoadingAll = false;
    }
  }

  findBlogSection(doc) {
    const sectionId = this.getAttribute('section-id');
    return (
      doc.querySelector(`kv-custom-blog[section-id="${sectionId}"]`) ||
      doc.querySelector('kv-custom-blog')
    );
  }

  appendItemsFromSection(section, maxToAdd = Infinity, source = 'all') {
    const incomingItems = section.querySelectorAll(
      '[ref="grid"] .kv-custom-blog__item, .kv-custom-blog__grid .kv-custom-blog__item'
    );
    const loadedUrls = this.getLoadedUrls();
    let addedCount = 0;

    incomingItems.forEach((item) => {
      if (addedCount >= maxToAdd) return;

      const articleUrl = this.normalizeArticleUrl(item.getAttribute('data-url'));
      if (!articleUrl || loadedUrls.has(articleUrl) || this.isFeaturedArticleUrl(articleUrl)) return;

      const clone = item.cloneNode(true);
      clone.classList.remove('kv-custom-blog__item--is-featured', 'kv-custom-blog__item--hidden');
      clone.hidden = false;
      clone.setAttribute('data-kv-source', source);
      clone.removeAttribute('data-initial');
      this.grid.appendChild(clone);
      loadedUrls.add(articleUrl);
      addedCount += 1;
    });

    return addedCount;
  }

  async fetchSectionHtml(url) {
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to load articles');
    return response.text();
  }

  async fetchSinglePage(maxToAdd = Infinity, source = 'all') {
    const nextPage = this.getNextPageFromLoadedCount();
    const nextUrl = nextPage ? this.getBlogPageUrl(nextPage) : this.loadMoreBtn?.getAttribute('data-next-url');

    if (!nextUrl) return 0;

    const html = await this.fetchSectionHtml(nextUrl);
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const nextSection = this.findBlogSection(doc);
    if (!nextSection) return 0;

    return this.appendItemsFromSection(nextSection, maxToAdd, source);
  }

  async fetchNextPage(source = 'all') {
    if (!this.hasMoreArticles()) {
      this.syncLoadMoreState();
      return 0;
    }

    const targetCount = this.getLoadMoreCount();
    let totalAdded = 0;
    let safety = 0;

    while (totalAdded < targetCount && this.hasMoreArticles() && safety < 15) {
      const remaining = targetCount - totalAdded;
      const added = await this.fetchSinglePage(remaining, source);
      totalAdded += added;
      safety += 1;

      if (added === 0 && !this.hasMoreArticles()) break;
      if (added === 0 && this.hasMoreArticles()) continue;
    }

    this.syncLoadMoreState();
    return totalAdded;
  }

  async onLoadMore(event) {
    event.preventDefault();

    if (!this.loadMoreBtn || !this.isLoadMoreAllowed()) return;
    if (!this.hasMoreArticles()) {
      this.syncLoadMoreState();
      return;
    }

    this.loadMoreBtn.setAttribute('aria-busy', 'true');
    this.loadMoreBtn.classList.add('kv-custom-blog__load-more--loading');

    try {
      await this.fetchNextPage('all');
      this.saveAllViewSnapshot();
      this.applyFilters();
    } catch (error) {
      console.error(error);
    } finally {
      if (this.loadMoreBtn) {
        this.loadMoreBtn.setAttribute('aria-busy', 'false');
        this.loadMoreBtn.classList.remove('kv-custom-blog__load-more--loading');
      }
    }
  }
}

if (!customElements.get('kv-custom-blog')) {
  customElements.define('kv-custom-blog', KvCustomBlog);
}
  